import React, { useEffect, useContext } from 'react';
import { LanguageContext, useNavigation } from './LanguageContext';

interface SeoProps {
    title: string;
    description: string;
    ogImage?: string;
    keywords?: string;
    jsonLd?: object | object[];
    canonicalUrl?: string;
    alternateUrls?: Record<string, string>;
    ogType?: 'website' | 'article';
}

const Seo: React.FC<SeoProps> = ({
    title,
    description,
    ogImage,
    keywords,
    jsonLd,
    canonicalUrl: canonicalOverride,
    alternateUrls,
    ogType = 'website',
}) => {
    const { language, languages } = useContext(LanguageContext);
    const { path } = useNavigation();

    // Fallback to a generic image if a specific one isn't provided.
    const defaultOgImage = `/images/logos/buildhub-global-logo-B.webp`;

    useEffect(() => {
        const baseUrl = window.location.origin;
        const canonicalPath = path === '/' ? '/' : path.replace(/\/+$/, '');
        const toAbsoluteUrl = (url: string) => url.startsWith('http') ? url : `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`;
        const defaultCanonicalUrl = `${baseUrl}${canonicalPath}${language !== 'en' ? `?lang=${language}` : ''}`;
        const canonicalUrl = canonicalOverride ? toAbsoluteUrl(canonicalOverride) : defaultCanonicalUrl;
        const finalOgImage = (ogImage && (ogImage.startsWith('http://') || ogImage.startsWith('https://')))
            ? ogImage
            : `${baseUrl}${ogImage || defaultOgImage}`;

        // Set document title
        document.title = title;

        // Helper to create or update meta tags
        const setMetaTag = (attr: 'name' | 'property', value: string, content: string) => {
            let element = document.querySelector(`meta[${attr}='${value}']`) as HTMLMetaElement;
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attr, value);
                document.head.appendChild(element);
            }
            element.setAttribute('content', content);
        };

        // Helper to create or update link tags
        const setLinkTag = (rel: string, href: string, hreflang?: string) => {
            const selector = hreflang ? `link[rel='${rel}'][hreflang='${hreflang}']` : `link[rel='${rel}']`;
            let element = document.querySelector(selector) as HTMLLinkElement;
            if (!element) {
                element = document.createElement('link');
                element.setAttribute('rel', rel);
                if (hreflang) {
                    element.setAttribute('hreflang', hreflang);
                }
                document.head.appendChild(element);
            }
            element.setAttribute('href', href);
            return element;
        };

        // Standard meta tags
        setMetaTag('name', 'google-site-verification', 'rQsD-yitR_Cs7_3PPgigSg');
        setMetaTag('name', 'description', description);
        if (keywords) {
            setMetaTag('name', 'keywords', keywords);
        }
        setMetaTag('name', 'robots', 'index, follow');

        // Canonical URL
        setLinkTag('canonical', canonicalUrl);

        // Hreflang tags - remove old ones before adding new ones
        const existingHreflangs = document.querySelectorAll("link[rel='alternate']");
        existingHreflangs.forEach(link => link.remove());

        if (alternateUrls) {
            Object.entries(alternateUrls).forEach(([lang, url]) => {
                setLinkTag('alternate', toAbsoluteUrl(url), lang);
            });
            const defaultUrl = alternateUrls.en || canonicalUrl;
            setLinkTag('alternate', toAbsoluteUrl(defaultUrl), 'x-default');
        } else {
            languages.forEach(lang => {
                const langPath = path === '/' ? '/' : path.replace(/\/+$/, '');
                const langUrl = `${baseUrl}${langPath}${lang.code !== 'en' ? `?lang=${lang.code}` : ''}`;
                setLinkTag('alternate', langUrl, lang.code);
            });
            setLinkTag('alternate', `${baseUrl}${path === '/' ? '/' : path.replace(/\/+$/, '')}`, 'x-default');
        }

        // Open Graph
        setMetaTag('property', 'og:title', title);
        setMetaTag('property', 'og:description', description);
        setMetaTag('property', 'og:url', canonicalUrl);
        setMetaTag('property', 'og:type', ogType);
        setMetaTag('property', 'og:image', finalOgImage);
        setMetaTag('property', 'og:locale', language.replace('-', '_'));

        // Twitter Card
        setMetaTag('name', 'twitter:card', 'summary_large_image');
        setMetaTag('name', 'twitter:title', title);
        setMetaTag('name', 'twitter:description', description);
        setMetaTag('name', 'twitter:image', finalOgImage);

        // JSON-LD - remove old one before adding new one
        let script = document.querySelector('script[type="application/ld+json"]');
        if (script) {
            script.remove();
        }
        if (jsonLd) {
            script = document.createElement('script');
            script.setAttribute('type', 'application/ld+json');
            script.textContent = JSON.stringify(jsonLd);
            document.head.appendChild(script);
        }

    }, [title, description, language, languages, path, ogImage, jsonLd, keywords, canonicalOverride, alternateUrls, ogType]);

    return null; // This component does not render anything to the DOM
};

export default Seo;
